import { createElement as rc, useMemo } from 'react';
import { View, styled, fromTheme, Card, Text, webOnlyStyles } from 'lib_ui-primitives';
import { useTheme } from 'styled-components';
import useColumnCell from '../columns/useColumnCell';
import { getRowHighlightStyle, getPillBoxStyle } from '../TableList/useRowDetail.shared';
import {
    resolveDisplayModifierColor,
    resolvePillBoxBorderColor
} from 'lib_ui-primitives/src/styling/themes/gl2025/displayModifierColors';

const CardListRow = styled(Card)`
    padding: 16px;
    background-color: ${fromTheme('cardList', 'backgroundColor')};
`;

const Field = styled(View).attrs({ name: 'cardbody-field' })`
    align-items: ${({ dataType }) => (dataType === 'patchDetail' ? 'stretch' : 'flex-start')};
    flex-grow: ${({ dataType }) => (dataType === 'patchDetail' ? 1 : 0)};
    min-width: ${({ dataType }) => (dataType === 'patchDetail' ? '100%' : 0)};
    flex-direction: column;
`;

const Label = styled(Text)`
    font-size: ${fromTheme('fontSizeLabel')};
    color: ${fromTheme('labelColor')};
    margin-bottom: ${fromTheme('textMargin')};
`;

let _CardBody = styled(Card.Body)`
    margin: ${fromTheme('viewMargin')};
    border-top-width: 0;
    flex-wrap: wrap;
    flex-direction: row;
`;

const LineBreak = styled(View).attrs({ name: 'line-break' })`
    flex-basis: 100%;
    border-top: ${({ hNode, theme }) => hNode.includeLine ? `1px solid ${theme.cardList.borderColor}` : 'none' };
`;

_CardBody = webOnlyStyles(_CardBody)`
    column-gap: ${fromTheme('viewMarginMore')};
`;

/**
 * This could be a normal component instead of a hook except that RowDetail is supplied
 * to lists as a property (which is a constraint that can't easily be changed). Creating
 * it as a hook allows the memoization of the property to be encapsulated.
 * @param {object} hNode - metadata
 * @returns CardBody component used as RowDetail in lists
 */
export default function useCardBody(hNode) {
    if (hNode.children == null) {
        throw new Error(`A ${hNode.hNodeType} must contain metadata columns.`);
    }
    const theme = useTheme();
    const darkMode = theme?.darkMode || false;

    return useMemo(() => {
        function CardBody(props) {
            const { item } = props || {};
            const columnHNodes = hNode.children.filter(c => c.hNodeTypeGroup === 'listColumn');

            // Display modifier: row highlight (card background)
            const highlightStyle = getRowHighlightStyle(item, columnHNodes, resolveDisplayModifierColor, darkMode, resolvePillBoxBorderColor);

            // prettier-ignore
            return rc(CardListRow, { style: highlightStyle },
                    rc(_CardBody, {key: item._id, id: `cardbody${item._id}`},
                        columnHNodes.map((colHNode, i) => {
                            if (colHNode.dataType === 'lineBreak') {
                                return rc(LineBreak, { key: i, hNode: colHNode });
                            }
                            // Display modifier: pill box (colored pill around value)
                            const pillStyle = getPillBoxStyle(item, colHNode, resolveDisplayModifierColor, darkMode, resolvePillBoxBorderColor);
                            return rc(Field, {key:i, dataType: colHNode.dataType},
                                rc(Label, null, colHNode.title + ':'),
                                pillStyle
                                    ? rc(View, { style: pillStyle },
                                        rc(Value, {item, hNode: {...hNode, ...colHNode}}))
                                    : rc(Value, {item, hNode: {...hNode, ...colHNode}})
                            );
                        })
                    )
                );
        }
        CardBody.getItemSize = () => {
            if (hNode.children.find(c => c.hNodeType === 'PatchDetail')) return 350;
            return 250;
        };
        return CardBody;
    }, [hNode, darkMode]);
}

export function Value(props) {
    let { item, hNode, style } = props;
    hNode.asCardBody = true;
    return useColumnCell(item, hNode, style, undefined, false, undefined, false);
}
